import React, { useState } from "react";
import Navbar from "../Components/Navbar";
import Sidebar from "../Components/Sidebar";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import CreateJobModal from '../Components/CreateJobModal';
import { getJobStats } from "../data/dummyJobs";
import axios from '../axios';
import { useEffect } from 'react';
import { useThemeContext } from '../context/ThemeContext';
import {
  Description as FileTextIcon,
  Schedule as ClockIcon,
  BuildCircle as WrenchIcon,
  AttachMoney as DollarIcon,
  WarningAmber as AlertIcon
} from "@mui/icons-material";

const Dashboard = () => {
  const { isDarkMode } = useThemeContext();
  const [jobsList, setJobsList] = useState([]);
  const [stockList, setStockList] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(true);
  const [todayIncome, setTodayIncome] = useState(0);
  const stats = getJobStats(jobsList);

  useEffect(() => {
    let mounted = true;
    const token = JSON.parse(localStorage.getItem('userInfo'))?.token;
    setJobsLoading(true);
    axios.get('/paneljob', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const payload = res.data;
        let docs = [];
        if (Array.isArray(payload)) docs = payload;
        else if (Array.isArray(payload?.data)) docs = payload.data;
        else if (Array.isArray(payload?.docs)) docs = payload.docs;
        else if (Array.isArray(payload?.result)) docs = payload.result;
        else docs = [];
        if (!mounted) return;
        const normalized = (docs || []).map(j => ({
          id: j._id || j.id,
          ref: j.jobNumber || j.jobCode || (j._id ? String(j._id).slice(-6) : ''),
          customer: j.customerName || j.customer || j.customerName || '',
          desc: j.faultDescription || j.description || j.desc || '',
          status: j.status || '',
          amount: j.roughCost || j.amount || j.totalAmount || 0,
          date: j.createdAt || j.date || ''
        }));
        setJobsList(normalized);
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setJobsLoading(false);
      })
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const token = JSON.parse(localStorage.getItem('userInfo'))?.token;
    axios.get('/sell/invoice/todayIncome', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!mounted) return;
        const total = res?.data?.total;
        setTodayIncome(typeof total === 'number' ? total : Number(total) || 0);
      })
      .catch(() => {
        if (mounted) setTodayIncome(0);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const token = JSON.parse(localStorage.getItem('userInfo'))?.token;
    setStockLoading(true);
    axios.get('/stock/allStock', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        if (!mounted) return;
        setStockList(data);
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setStockLoading(false);
      });
    return () => { mounted = false; };
  }, []);
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const userTypeStr = (userInfo.userType || userInfo.role || '').toString().toLowerCase();
  const isTechnician = userTypeStr === 'technician';

  const handleCreateFromDashboard = (job) => {
    // send the created job to Jobs page so it can add to its local list
    navigate('/jobs', { state: { newJob: job } });
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc",
          minHeight: "100vh",
          pt: "80px",
          display: "flex",
          fontFamily: "Inter, system-ui, sans-serif"
        }}
      >
        <Sidebar />

        {/* MAIN CONTENT */}
        <Box sx={{ flexGrow: 1, px: 4 }}>
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 600, color: isDarkMode ? "#f1f5f9" : "#0f172a" }}>
                Welcome back!
              </Typography>
              <Typography sx={{ fontSize: 14, color: isDarkMode ? "#cbd5e1" : "#64748b" }}>
                Here's what's happening with your repair shop today.
              </Typography>
            </Box>

            {!isTechnician && (
              <Button
                onClick={() => setCreateOpen(true)}
                sx={{
                  backgroundColor: "#2563eb",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "8px",
                  px: 2,
                  "&:hover": { backgroundColor: "#1d4ed8" }
                }}
              >
                + Create Job
              </Button>
            )}
          </Box>

          {/* STATS */}
          <Grid container spacing={3}>
            {[
              {
                title: "Total Jobs",
                value: stats.total,
                icon: <FileTextIcon sx={{ color: "#2563eb" }} />,
                bg: "#eff6ff"
              },
              {
                title: "Pending",
                value: stats.pending,
                icon: <ClockIcon sx={{ color: "#f59e0b" }} />,
                bg: "#fef3c7"
              },
              {
                title: "In Progress",
                value: stats.inProgress,
                icon: <WrenchIcon sx={{ color: "#f97316" }} />,
                bg: "#ffedd5"
              },
              {
                title: "Today's Income",
                value: `Rs. ${todayIncome}`,
                icon: <DollarIcon sx={{ color: "#16a34a" }} />,
                bg: "#ecfdf5"
              }
            ].map((item, i) => (
              <Grid item xs={12} md={3} key={i}>
                <Card
                  onClick={() => {
                    // navigate to jobs page with a status filter when applicable
                    if (item.title === 'Total Jobs') return navigate('/jobs');
                    if (item.title === "Today's Income") return navigate('/invoices');
                    // for other stat cards use title as status filter
                    navigate(`/jobs?status=${encodeURIComponent(item.title)}`);
                  }}
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    cursor: 'pointer',
                    '&:hover': { boxShadow: '0 4px 14px rgba(0,0,0,0.06)' }
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
                        {item.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: item.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {item.icon}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* LOWER SECTION */}
          <Grid container spacing={3} mt={1}>
            {/* RECENT JOBS */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e5e7eb" }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography fontWeight={600}>Recent Jobs</Typography>
                    <Button
                      sx={{ textTransform: "none", color: isDarkMode ? "#fff" : "#2563eb", fontSize: 13 }}
                      onClick={() => navigate('/jobs')}
                    >
                      View all ↗
                    </Button>
                  </Box>

                  {jobsLoading ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    jobsList.slice(0, 2).map((j) => (
                    <Card
                      key={j.id}
                      onClick={() => navigate(`/jobs?ref=${encodeURIComponent(j.ref)}`)}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: "10px",
                        border: "1px solid #e5e7eb",
                        cursor: 'pointer',
                        '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.06)' }
                      }}
                    >
                      <Box display="flex" justifyContent="space-between">
                        <Box>
                          <Typography fontWeight={500}>{j.ref}</Typography>
                          <Typography fontSize={13} color="#64748b">
                            {j.customer}
                          </Typography>
                          <Typography fontSize={12} color="#94a3b8">
                            {j.desc}
                          </Typography>
                        </Box>

                        {(() => {
                          const s = (j.status || '').toString().toLowerCase();
                          const bg = isDarkMode 
                            ? (s.includes('ready') || s.includes('completed') ? '#166534' : (s.includes('in progress') || s.includes('inprogress') ? '#134e4a' : (s.includes('pending') ? '#92400e' : '#5b21b6')))
                            : (s.includes('ready') || s.includes('completed') ? '#ecfdf5' : (s.includes('in progress') || s.includes('inprogress') ? '#f0fdf4' : (s.includes('pending') ? '#fffbeb' : '#ede9fe')));
                          const color = isDarkMode 
                            ? (s.includes('ready') || s.includes('completed') ? '#bbf7d0' : (s.includes('in progress') || s.includes('inprogress') ? '#99f6e4' : (s.includes('pending') ? '#fcd34d' : '#d8b4fe')))
                            : (s.includes('ready') || s.includes('completed') ? '#16a34a' : (s.includes('in progress') || s.includes('inprogress') ? '#15803d' : (s.includes('pending') ? '#92400e' : '#6d28d9')));
                          return <Chip label={j.status} sx={{ backgroundColor: bg, color, fontSize: 12, fontWeight: 500 }} />
                        })()}
                      </Box>
                    </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            </Grid>

          
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e5e7eb" }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Box display="flex" gap={1} alignItems="center">
                      <AlertIcon sx={{ color: "#f97316" }} />
                      <Typography fontWeight={600}>Low Stock Alerts</Typography>
                    </Box>
                    <Button
                      sx={{ textTransform: "none", color: isDarkMode ? "#fff" : "#2563eb", fontSize: 13 }}
                      onClick={() => navigate('/jobsearch')}
                    >
                      View all ↗
                    </Button>
                  </Box>

                  {stockLoading ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    (stockList.length ? (
                      // sort by totalQuantity ascending and take 2 lowest
                      stockList.sort((a,b) => (a.totalQuantity||0) - (b.totalQuantity||0)).slice(0,2)
                    ) : [/* fallback empty */]).map((s, i) => (
                    <Card
                      key={s?._id || i}
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: "10px",
                        border: isDarkMode ? "1px solid #78350f" : "1px solid #fed7aa",
                        backgroundColor: isDarkMode ? "#7c2d12" : "#fff7ed"
                      }}
                    >
                      <Box display="flex" justifyContent="space-between">
                        <Box>
                          <Typography fontWeight={500} sx={{ color: isDarkMode ? '#f1f5f9' : 'inherit' }}>
                            {s?.name || 'Unknown Item'}
                          </Typography>
                          <Typography fontSize={12} sx={{ color: isDarkMode ? "#cbd5e1" : "#64748b" }}>
                            {s?._id ? `ID: ${String(s._id).slice(-6)}` : ''}
                          </Typography>
                        </Box>

                        <Box textAlign="right">
                          <Typography fontWeight={600} sx={{ color: isDarkMode ? "#fed7aa" : "#ea580c" }}>
                            {s ? `${s.totalQuantity || 0} left` : ''}
                          </Typography>
                          <Typography fontSize={12} sx={{ color: isDarkMode ? "#fbbf24" : "#fb923c" }}>
                            Min: 2
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      <CreateJobModal open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreateFromDashboard} />
      </Box>
    </>
  );
};

export default Dashboard;
